import { useCallback, useEffect, useMemo, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import type { MapRef } from 'react-map-gl/mapbox';
import { useDispatch, useSelector } from 'react-redux';
import useUser from '@alltrails/context/hooks/useUser';
import { SPACE_50 } from '@alltrails/denali/tokens';
import SignUpModal from '@alltrails/user-auth/components/SignUpModal';
import DistanceAwayUpsellModal from '../components/DistanceAwayUpsellModal/DistanceAwayUpsellModal';
import { ExploreServiceState, updateDistanceAway } from '../redux/exploreServiceSlice';
import DistanceAway from '../types/DistanceAway';

/**
 * Only to be used by ExploreFilterBar and AllFiltersModal - this hook really is just a way to consolidate duplicate code and logic
 * and let the two mentioned components render a distance away filter for explore in slightly different manners.
 *
 * @param map Mapbox map object
 * @param defaultDistanceAwayMeters default meters to use for the distance away slider when a value hasn't been set
 * @returns an object of values, callbacks, and components to be used by ExploreFilterBar and AllFiltersModal
 */
const useDistanceAwayFilter = (map?: MapRef, defaultDistanceAwayMeters?: number) => {
  const user = useUser();
  const dispatch = useDispatch();
  const { distanceAway, locationResult } = useSelector((state: { exploreService: ExploreServiceState }) => ({
    distanceAway: state.exploreService.distanceAway,
    locationResult: state.exploreService.locationResult
  }));

  // We store a local value for distance away so a user can slide the slider w/o triggering a ton of isochrone requests
  const [localDistanceAway, setLocalDistanceAway] = useState<number | undefined>();

  useEffect(() => {
    setLocalDistanceAway(distanceAway?.distance || defaultDistanceAwayMeters);
  }, [distanceAway?.distance, defaultDistanceAwayMeters]);

  const [signUpPendingDistanceAway, setSignUpPendingDistanceAway] = useState<DistanceAway>();

  const [isDistanceAwayUpsellModalOpen, setIsDistanceAwayUpsellModalOpen] = useState(false);
  const onDistanceAwayUpsellModalCloseRequest = useCallback(() => {
    setIsDistanceAwayUpsellModalOpen(false);
  }, []);

  const updateDistanceAwayWithAuth = useCallback(
    (newDistanceAway: DistanceAway | undefined) => {
      if (user?.featureEntitlements?.includes('distance_away')) {
        dispatch(updateDistanceAway(newDistanceAway));
      } else if (!user) {
        setSignUpPendingDistanceAway(newDistanceAway);
      } else {
        setIsDistanceAwayUpsellModalOpen(true);
      }
    },
    [dispatch, user]
  );

  const applyDistanceAway = useCallback(
    (newDistance: number | undefined, skipAuth = false) => {
      let newDistanceAway: DistanceAway | undefined;
      if (newDistance === undefined) {
        newDistanceAway = undefined;
        setLocalDistanceAway(defaultDistanceAwayMeters);
      } else if (distanceAway) {
        newDistanceAway = { center: distanceAway.center, distance: newDistance };
      } else if (map) {
        newDistanceAway = { center: map.getCenter(), distance: newDistance };
      }
      if (skipAuth) {
        dispatch(updateDistanceAway(newDistanceAway));
      } else {
        updateDistanceAwayWithAuth(newDistanceAway);
      }
    },
    [defaultDistanceAwayMeters, dispatch, distanceAway, map, updateDistanceAwayWithAuth]
  );

  const isDistanceAwaySignUpModalOpen = Boolean(signUpPendingDistanceAway);
  const distanceAwaySignUpModal = useMemo(
    () => (
      <SignUpModal
        closeModal={() => setSignUpPendingDistanceAway(undefined)}
        isOpen={isDistanceAwaySignUpModalOpen}
        onSuccess={user => {
          if (user?.featureEntitlements?.includes('distance_away')) {
            updateDistanceAway(signUpPendingDistanceAway);
          } else {
            setIsDistanceAwayUpsellModalOpen(true);
          }
          setSignUpPendingDistanceAway(undefined);
        }}
        trigger="Distance Away"
        upsellTrigger="Distance Away"
      />
    ),
    [isDistanceAwaySignUpModalOpen, signUpPendingDistanceAway]
  );

  const distanceAwayUpsellModal = useMemo(
    () => <DistanceAwayUpsellModal onCloseRequest={onDistanceAwayUpsellModalCloseRequest} isOpen={isDistanceAwayUpsellModalOpen} />,
    [isDistanceAwayUpsellModalOpen, onDistanceAwayUpsellModalCloseRequest]
  );

  const distanceAwaySliderSubHeader = useMemo(() => {
    const locationText = locationResult ? locationResult.name : <FormattedMessage defaultMessage="Map location" />;
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: SPACE_50 }}>
        <div>
          <FormattedMessage defaultMessage="Driving distance from:" />
        </div>
        <div>{locationText}</div>
      </div>
    );
  }, [locationResult]);

  return useMemo(
    () => ({
      applyDistanceAway,
      distanceAway,
      distanceAwaySliderSubHeader,
      distanceAwaySignUpModal,
      distanceAwayUpsellModal,
      isDistanceAwaySignUpModalOpen,
      isDistanceAwayUpsellModalOpen,
      localDistanceAway,
      locationResult,
      setLocalDistanceAway,
      updateDistanceAwayWithAuth
    }),
    [
      applyDistanceAway,
      distanceAway,
      distanceAwaySliderSubHeader,
      distanceAwaySignUpModal,
      distanceAwayUpsellModal,
      isDistanceAwaySignUpModalOpen,
      isDistanceAwayUpsellModalOpen,
      localDistanceAway,
      locationResult,
      setLocalDistanceAway,
      updateDistanceAwayWithAuth
    ]
  );
};

export default useDistanceAwayFilter;
